import { Box, darken, styled } from '@mui/material';
import { CarIcon, isAllowedPaintModel } from './models';
import { isTooDarkVehicleColor } from './models/utils';
import { useAnimationPermissions, useDesignPackage } from 'console/shared/hooks/helpers';
import { useMemo } from 'react';
const hide = { display: 'none !important' };
const show = { display: 'block !important' };
export const CarModel = styled(CarIcon)(({ theme, frontLeftOpen, frontRightOpen, online, rearLeftOpen, rearRightOpen, hood, trunk, smoke, rotate_mirror, mirror, auto_ignition, car_color, siren, demo, model, }) => {
    const animation_permissions = useAnimationPermissions();
    const allow_design_package = useDesignPackage();
    const anim_smoke = useMemo(() => demo ? true : allow_design_package && animation_permissions.car_smoke, [demo, allow_design_package, animation_permissions.car_smoke]);
    const anim_car_autorun = useMemo(() => demo ? true : allow_design_package && animation_permissions.car_autorun, [demo, allow_design_package, animation_permissions.car_autorun]);
    const car_lock = useMemo(() => demo ? true : allow_design_package && animation_permissions.car_lock, [demo, allow_design_package, animation_permissions.car_lock]);
    const car_siren = useMemo(() => demo ? true : allow_design_package && animation_permissions.car_siren, [demo, allow_design_package, animation_permissions.car_siren]);
    const design_package = demo ? true : allow_design_package;
    const isDark = theme.palette.mode === 'dark';
    const isTooDark = isTooDarkVehicleColor(theme, car_color?.body);
    const allowed_paint_model = isAllowedPaintModel(model, design_package);
    const stroke_color = isTooDark ? '#fff' : '#333';
    return {
        height: '100%',
        opacity: online ? 1 : 0.3, //0.3,
        '& #car-front-left-open': hide,
        '& #car-front-right-open': hide,
        '& #car-rear-left-open': hide,
        '& #car-rear-right-open': hide,
        '& #car-hood': hide,
        '& #car-trunk': hide,
        '& .car-smoke': hide,
        '& #car-trunk-closed': show,
        '& .car-no-anim-smoke': hide,
        '& #car-siren1, #car-siren2, #car-siren3, #car-siren4, #car-siren5': {
            opacity: 0,
            stroke: isDark ? '#fff' : '#333',
        },
        '& #car-left-rear-light, #car-right-rear-light, #car-left-rear-main-light, #car-right-rear-main-light': {
            fill: 'tomato',
            fillOpacity: 0.3,
        },
        '& #car-left-front-light, #car-right-front-light': {
            fill: '#ff8400',
            fillOpacity: 0.2,
        },
        '& #car-right-front-main-light, #car-left-front-main-light': {
            fill: '#d4b72a',
            fillOpacity: 0.2,
        },
        '& #car-left-signal-light, #car-right-signal-light': {
            strokeWidth: 0,
        },
        ...(isDark && {
            '& .car-glass': {
                fill: '#707070',
            },
        }),
        transition: 'transform 0.2s',
        ...(theme.palette.mode == 'dark' && {
            '& [fill="#fff"]': {
                fill: '#333',
            },
            '& [fill="#202020"]': {
                fill: '#fff',
            },
            '& [stroke="#202020"]': {
                stroke: '#ddd',
            },
            '& stop[offset="0"]': {
                stopColor: '#fff',
                stopOpacity: 0.1,
            },
            '& stop[offset="1"]': {
                stopColor: '#88e',
                stopOpacity: 0.0,
            },
            '& #car-interior-bg, #car-interior-bg-1': {
                fill: '#2c2c2c',
            },
            '& #car-rear-seats, #car-rear-seats-1, #car-rear-seats-2, #car-rear-seats-3, #car-front-seats, #car-front-seats-1': {
                fill: '#222',
            },
        }),
        ...(allowed_paint_model && {
            '& [stroke="#202020"]': {
                stroke: stroke_color,
            },
            '& #car-interior-bg, #car-interior-bg-1': {
                fill: '#9b9b9bff',
            },
        }),
        ...(frontLeftOpen && {
            '& #car-front-left-closed': hide,
            '& #car-front-left-open': show,
        }),
        ...(frontRightOpen && {
            '& #car-front-right-closed': hide,
            '& #car-front-right-open': show,
        }),
        ...(rearLeftOpen && {
            '& #car-rear-left-closed': hide,
            '& #car-rear-left-open': show,
        }),
        ...(rearRightOpen && {
            '& #car-rear-right-closed': hide,
            '& #car-rear-right-open': show,
        }),
        ...(hood && {
            '& #car-hood': {
                '& .car-main-hood': {
                    fill: `rgb(150, 78, 78) !important`,
                },
            },
            ...(design_package && {
                '& .car-main-hood': {
                    fill: car_color?.body
                        ? darken(car_color?.body, 0.5)
                        : undefined,
                },
            }),
        }),
        ...(trunk && {
            '& #car-trunk': show,
            '& #car-trunk-closed': hide,
            ...(design_package && {
                '& .car-main-trunk': {
                    fill: car_color?.body
                        ? darken(car_color?.body, 0.5)
                        : undefined,
                },
            }),
        }),
        ...(!anim_smoke && smoke && { '& .car-no-anim-smoke': show }),
        ...(anim_smoke &&
            smoke && {
            '& .car-smoke': {
                '& .car-smoke1': {
                    animation: isDark
                        ? 'waveAlarmDark 1.5s infinite'
                        : 'waveAlarm 1.5s infinite',
                    strokeWidth: '0',
                },
                '& .car-smoke2': {
                    animation: isDark
                        ? 'waveAlarmDark 1.5s infinite'
                        : 'waveAlarm 1.5s infinite',
                    strokeWidth: '0',
                    animationDelay: '0.15s',
                },
                '& .car-smoke3': {
                    animation: isDark
                        ? 'waveAlarmDark 1.5s infinite'
                        : 'waveAlarm 1.5s infinite',
                    strokeWidth: '0',
                    animationDelay: '0.3s',
                },
                '@keyframes waveAlarm': {
                    '0%': {
                        fill: '#ededed',
                    },
                    '15%': {
                        fill: '#dedede',
                    },
                    '30%': {
                        fill: '#d4d4d4',
                    },
                    '40%': {
                        fill: '#cccccc',
                    },
                    '50%': {
                        fill: '#c4c4c4',
                    },
                    '60%': {
                        fill: '#cccccc',
                    },
                    '70%': {
                        fill: '#d4d4d4',
                    },
                    '85%': {
                        fill: '#dedede',
                    },
                    '100%': {
                        fill: '#ededed',
                    },
                },
                '@keyframes waveAlarmDark': {
                    '0%': {
                        fill: '#333',
                    },
                    '15%': {
                        fill: '#3d3d3d',
                    },
                    '30%': {
                        fill: '#454444',
                    },
                    '40%': {
                        fill: '#828282',
                    },
                    '50%': {
                        fill: '#9e9e9e',
                    },
                    '60%': {
                        fill: '#828282',
                    },
                    '70%': {
                        fill: '#454444',
                    },
                    '85%': {
                        fill: '#3d3d3d',
                    },
                    '100%': {
                        fill: '#333',
                    },
                },
                ...show,
            },
            '& #car-left-rear-light, #car-right-rear-light': {
                fill: 'tomato',
                fillOpacity: 0.6,
            },
            '& #car-left-rear-main-light, #car-right-rear-main-light': {
                fill: 'tomato',
                fillOpacity: 0.6,
            },
            '& #car-left-front-light, #car-right-front-light': {
                fill: '#ff8400',
                fillOpacity: 0.6,
            },
            '& #car-right-front-main-light, #car-left-front-main-light': {
                fill: '#d4b72a',
                fillOpacity: 0.6,
            },
        }),
        ...(rotate_mirror && {
            transform: 'rotate(-180deg)',
        }),
        ...(mirror && {
            transform: 'scale(-1, 1)',
        }),
        ...(car_lock && {
            '& #car-left-rear-main-light, #car-right-rear-main-light, #car-left-rear-light, #car-right-rear-light': {
                animation: `AlarmRareLight 0.5s 2`,
            },
            '& #car-left-front-light': {
                animation: `AlarmFrontLight 0.5s 2`,
            },
            '& #car-right-front-light': {
                animation: `AlarmFrontLight 0.5s 2`,
            },
            '& #car-right-front-main-light': {
                animation: `AlarmFrontMainLight 0.5s 2`,
            },
            '& #car-left-front-main-light': {
                animation: `AlarmFrontMainLight 0.5s 2`,
            },
        }),
        '@keyframes AlarmRareLight': {
            '0%': {
                fill: 'tomato',
                fillOpacity: 0.2,
            },
            '50%': {
                fill: 'red',
                fillOpacity: 1,
            },
            '100%': {
                fill: 'tomato',
                fillOpacity: 0.6,
            },
        },
        ...(anim_car_autorun &&
            auto_ignition && {
            '& #car-right-front-main-light': {
                animation: `AutoIgnitionAlarmLight 5s infinite`,
            },
            '& #car-left-front-main-light': {
                animation: `AutoIgnitionAlarmLight 5s infinite`,
            },
            '& #car-left-signal-light, #car-right-signal-light': {
                strokeWidth: 0,
                animation: `AutoIgnitionAlarmSignalLight 5s infinite`,
            },
        }),
        '@keyframes AutoIgnitionAlarmSignalLight': {
            '0%': {
                fill: '#fffb00',
                fillOpacity: 0.5,
            },
            '20%': {
                fill: '#fffb00',
                fillOpacity: 0,
            },
            '100%': {
                fill: '#fffb00',
                fillOpacity: 0,
            },
        },
        '@keyframes AutoIgnitionAlarmLight': {
            '0%': {
                fill: '#fffb00',
                fillOpacity: 1,
            },
            '20%': {
                fill: '#fffb00',
                fillOpacity: 0.2,
            },
            '100%': {
                fill: '#fffb00',
                fillOpacity: 0.2,
            },
        },
        '@keyframes AlarmFrontLight': {
            '0%': {
                fill: '#ff8400',
                fillOpacity: 0.2,
            },
            '50%': {
                fill: '#ff8400',
                fillOpacity: 1,
            },
            '100%': {
                fill: '#ff8400',
                fillOpacity: 0.6,
            },
        },
        '@keyframes AlarmFrontMainLight': {
            '0%': {
                fill: '#fffb00',
                fillOpacity: 0.2,
            },
            '50%': {
                fill: '#fffb00',
                fillOpacity: 1,
            },
            '100%': {
                fill: '#fffb00',
                fillOpacity: 0.2,
            },
        },
        ...(design_package &&
            car_color?.body && {
            '& .car-body': {
                fill: car_color.body,
            },
            '& .exhaust_pipe': {
                fill: '#d4d4d4',
            },
        }),
        ...(design_package &&
            car_color?.glass && {
            '& .car-glass': {
                fill: car_color.glass,
            },
        }),
        '@keyframes AlarmSiren': {
            '0%': {
                opacity: 1,
            },
            '50%': {
                opacity: 0,
            },
            '100%': {
                opacity: 1,
            },
        },
        ...(car_siren &&
            siren && {
            '& #car-siren1': {
                animation: 'AlarmSiren 2s infinite',
            },
            '& #car-siren2': {
                animation: 'AlarmSiren 2s infinite',
                animationDelay: '0.25s',
            },
            '& #car-siren3': {
                animation: 'AlarmSiren 2s infinite',
                animationDelay: '0.5s',
            },
            '& #car-siren4': {
                animation: 'AlarmSiren 2s infinite',
                animationDelay: '0.75s',
            },
            '& #car-siren5': {
                animation: 'AlarmSiren 2s infinite',
                animationDelay: '1s',
            },
        }),
    };
});
export const ModelView = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'center',
})(({ center }) => ({
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    '& svg': {
        maxWidth: '100%',
    },
    ...(center && { textAlign: 'center' }),
}));
